import { useState } from 'react';
import { useCart } from '@/hooks/useCart';
import type { Product } from '@/mocks/products';

interface ProductDetailsProps {
  product: Product;
}

function formatPrice(amount: number): string {
  return `KSh ${amount.toLocaleString('en-KE')}`;
}

function getDiscountPercent(price: number, salePrice: number | null): number | null {
  if (!salePrice || salePrice >= price) return null;
  return Math.round(((price - salePrice) / price) * 100);
}

export default function ProductDetails({ product }: ProductDetailsProps) {
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const { addToCart } = useCart();

  const discount = getDiscountPercent(product.price, product.salePrice);
  const displayPrice = product.salePrice ?? product.price;
  const originalPrice = product.salePrice ? product.price : null;

  const handleAddToCart = () => {
    addToCart(product.id, quantity);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const increment = () => setQuantity((q) => Math.min(q + 1, product.stock));
  const decrement = () => setQuantity((q) => Math.max(q - 1, 1));

  return (
    <div className="flex flex-col gap-4">
      {/* Brand & Store */}
      <div className="flex items-center gap-3">
        <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
          {product.brand}
        </span>
        <a
          href={`/seller/${product.storeName.toLowerCase().replace(/\s+/g, '-')}`}
          className="flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
        >
          <i className="ri-store-line" />
          {product.storeName}
        </a>
      </div>

      {/* Title */}
      <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading leading-snug">
        {product.name}
      </h1>

      {/* Rating */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <i
              key={star}
              className={`ri-star-fill text-sm ${
                star <= Math.round(product.rating) ? 'text-accent-500' : 'text-background-300'
              }`}
            />
          ))}
        </div>
        <span className="text-sm font-medium text-foreground-800">{product.rating}</span>
        <a href="#reviews" className="text-sm text-primary-600 hover:text-primary-700 transition-colors">
          ({product.reviewCount} reviews)
        </a>
      </div>

      {/* Price */}
      <div className="flex flex-wrap items-baseline gap-3 py-2">
        <span className="text-3xl md:text-4xl font-bold text-primary-600 font-heading">
          {formatPrice(displayPrice)}
        </span>
        {originalPrice && (
          <>
            <span className="text-lg text-foreground-500 line-through">{formatPrice(originalPrice)}</span>
            {discount && discount > 0 && (
              <span className="px-2 py-0.5 rounded-md bg-accent-500 text-foreground-950 text-sm font-bold">
                Save {discount}%
              </span>
            )}
          </>
        )}
      </div>

      {/* Stock status */}
      <div className="flex items-center gap-2 text-sm">
        {product.stock > 10 ? (
          <>
            <i className="ri-check-line text-green-600" />
            <span className="text-green-700 font-medium">In Stock</span>
            <span className="text-foreground-500">({product.stock} available)</span>
          </>
        ) : product.stock > 0 ? (
          <>
            <i className="ri-alert-line text-accent-600" />
            <span className="text-accent-700 font-medium">Low Stock</span>
            <span className="text-foreground-500">Only {product.stock} left</span>
          </>
        ) : (
          <>
            <i className="ri-close-line text-primary-600" />
            <span className="text-primary-700 font-medium">Out of Stock</span>
          </>
        )}
      </div>

      {/* SKU */}
      <div className="text-xs text-foreground-500">
        SKU: <span className="font-mono">{product.sku}</span>
      </div>

      {/* Divider */}
      <div className="h-px bg-background-200/70" />

      {/* Quantity & Add to Cart */}
      {product.stock > 0 && (
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex items-center rounded-lg border border-background-200/70 overflow-hidden">
            <button
              type="button"
              onClick={decrement}
              className="w-10 h-11 flex items-center justify-center hover:bg-background-100 transition-colors"
              aria-label="Decrease quantity"
            >
              <i className="ri-subtract-line text-foreground-700" />
            </button>
            <span className="w-12 h-11 flex items-center justify-center text-sm font-semibold text-foreground-900">
              {quantity}
            </span>
            <button
              type="button"
              onClick={increment}
              className="w-10 h-11 flex items-center justify-center hover:bg-background-100 transition-colors"
              aria-label="Increase quantity"
            >
              <i className="ri-add-line text-foreground-700" />
            </button>
          </div>
          <button
            type="button"
            onClick={handleAddToCart}
            className={`flex-1 h-11 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
              added
                ? 'bg-green-600 text-background-50'
                : 'bg-primary-500 text-background-50 hover:bg-primary-600'
            }`}
          >
            <i className={added ? 'ri-check-line' : 'ri-shopping-cart-line'} />
            {added ? 'Added to Cart!' : 'Add to Cart'}
          </button>
          <button
            type="button"
            className="h-11 px-5 rounded-lg bg-accent-500 text-foreground-950 font-semibold text-sm hover:bg-accent-600 transition-colors whitespace-nowrap"
          >
            Buy Now
          </button>
        </div>
      )}

      {/* Delivery info */}
      <div className="rounded-xl bg-background-100 p-4 space-y-2">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-truck-line text-primary-600 text-sm" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground-800">Fast Delivery</p>
            <p className="text-xs text-foreground-500">
              Delivery within 1-3 days in Nairobi, 2-5 days countrywide
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-shield-check-line text-accent-700 text-sm" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground-800">Secure Payment</p>
            <p className="text-xs text-foreground-500">
              M-Pesa, Card, Cash on Delivery accepted
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-loop-left-line text-secondary-700 text-sm" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground-800">Easy Returns</p>
            <p className="text-xs text-foreground-500">
              7-day return policy for eligible items
            </p>
          </div>
        </div>
      </div>

      {/* Tags */}
      {product.tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {product.tags.map((tag) => (
            <span
              key={tag}
              className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}